import { useEffect } from "react";
import { listen } from "@tauri-apps/api/event";
import { create } from "zustand";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

type State = {
  /** The display under the pointer; null until the tracker first reports. */
  activeDisplayId: number | null;
  cursorX: number;
  cursorY: number;
  /** When the tracker last saw the pointer move (`Date.now()`), if it has. */
  pointerMovedAt: number | null;
};

export const useActiveDisplay = create<State>(() => ({
  activeDisplayId: null,
  cursorX: 0,
  cursorY: 0,
  pointerMovedAt: null,
}));

type PointerSample = { displayId: number; x: number; y: number };

/** One tracker sample; a changed position after the first one is a move. */
function adoptPointerSample({ displayId, x, y }: PointerSample) {
  useActiveDisplay.setState((state) => ({
    activeDisplayId: displayId,
    cursorX: x,
    cursorY: y,
    pointerMovedAt:
      state.activeDisplayId !== null &&
      (state.cursorX !== x || state.cursorY !== y)
        ? Date.now()
        : state.pointerMovedAt,
  }));
}

export function useActiveDisplaySubscription() {
  useEffect(() => {
    let cancelled = false;
    const unlistens: Array<() => void> = [];
    const addUnlisten = (unlisten: () => void) => {
      if (cancelled) safeTauriUnlisten(unlisten);
      else unlistens.push(unlisten);
    };

    listen<PointerSample>("active-display-changed", (e) =>
      adoptPointerSample(e.payload),
    ).then(addUnlisten);
    listen<PointerSample>("cursor-position", (e) =>
      adoptPointerSample(e.payload),
    ).then(addUnlisten);
    return () => {
      cancelled = true;
      unlistens.forEach(safeTauriUnlisten);
    };
  }, []);
}
